// Small, dependency-free statistics for the iViz page's interactive playground.
// Sample data is generated in the browser; every result shown is computed here.

export type Rng = () => number;

export function CreateRng(seed: number): Rng {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 2 ** 32;
  };
}

export function SampleNormal(rng: Rng, mean: number, sd: number): number {
  const u = Math.max(rng(), 1e-12);
  return mean + sd * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * rng());
}

export function Mean(xs: number[]): number {
  return xs.reduce((sum, x) => sum + x, 0) / xs.length;
}

export function Variance(xs: number[]): number {
  const m = Mean(xs);
  return xs.reduce((sum, x) => sum + (x - m) ** 2, 0) / (xs.length - 1);
}

export interface FiveNumber { min: number; q1: number; median: number; q3: number; max: number }

function Quantile(sorted: number[], p: number): number {
  const i = (sorted.length - 1) * p;
  const lo = Math.floor(i);
  return sorted[lo] + (sorted[Math.ceil(i)] - sorted[lo]) * (i - lo);
}

export function FiveNumberSummary(xs: number[]): FiveNumber {
  const s = [...xs].sort((a, b) => a - b);
  return { min: s[0], q1: Quantile(s, 0.25), median: Quantile(s, 0.5), q3: Quantile(s, 0.75), max: s[s.length - 1] };
}

// Lanczos approximation of ln Γ(x).
function LogGamma(x: number): number {
  const c = [76.18009172947146, -86.50532032941677, 24.01409824083091, -1.231739572450155, 0.1208650973866179e-2, -0.5395239384953e-5];
  let y = x;
  const tmp = x + 5.5 - (x + 0.5) * Math.log(x + 5.5);
  let ser = 1.000000000190015;
  for (const ci of c) ser += ci / ++y;
  return -tmp + Math.log((2.5066282746310005 * ser) / x);
}

// Continued fraction for the regularized incomplete beta function (Numerical Recipes).
function BetaContinuedFraction(a: number, b: number, x: number): number {
  let c = 1, d = 1 - ((a + b) * x) / (a + 1);
  d = 1 / (Math.abs(d) < 1e-30 ? 1e-30 : d);
  let h = d;
  for (let m = 1; m <= 200; m++) {
    for (const aa of [(m * (b - m) * x) / ((a + 2 * m - 1) * (a + 2 * m)), (-(a + m) * (a + b + m) * x) / ((a + 2 * m) * (a + 2 * m + 1))]) {
      d = 1 + aa * d; d = 1 / (Math.abs(d) < 1e-30 ? 1e-30 : d);
      c = 1 + aa / c; c = Math.abs(c) < 1e-30 ? 1e-30 : c;
      h *= d * c;
    }
  }
  return h;
}

function RegularizedBeta(x: number, a: number, b: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const front = Math.exp(LogGamma(a + b) - LogGamma(a) - LogGamma(b) + a * Math.log(x) + b * Math.log(1 - x));
  return x < (a + 1) / (a + b + 2) ? (front * BetaContinuedFraction(a, b, x)) / a : 1 - (front * BetaContinuedFraction(b, a, 1 - x)) / b;
}

function StudentTTwoSidedP(t: number, df: number): number {
  return RegularizedBeta(df / (df + t * t), df / 2, 0.5);
}

export function WelchTTest(a: number[], b: number[]) {
  const va = Variance(a) / a.length;
  const vb = Variance(b) / b.length;
  const t = (Mean(a) - Mean(b)) / Math.sqrt(va + vb);
  const df = (va + vb) ** 2 / (va ** 2 / (a.length - 1) + vb ** 2 / (b.length - 1));
  return { t, df, p: StudentTTwoSidedP(t, df) };
}

export function Pearson(xs: number[], ys: number[]) {
  const mx = Mean(xs), my = Mean(ys);
  let sxy = 0, sxx = 0, syy = 0;
  xs.forEach((x, i) => { sxy += (x - mx) * (ys[i] - my); sxx += (x - mx) ** 2; syy += (ys[i] - my) ** 2; });
  const r = sxy / Math.sqrt(sxx * syy);
  const df = xs.length - 2;
  return { r, p: StudentTTwoSidedP(r * Math.sqrt(df / (1 - r * r)), df) };
}

export function LinearFit(xs: number[], ys: number[]) {
  const mx = Mean(xs), my = Mean(ys);
  let sxy = 0, sxx = 0;
  xs.forEach((x, i) => { sxy += (x - mx) * (ys[i] - my); sxx += (x - mx) ** 2; });
  const slope = sxy / sxx;
  const intercept = my - slope * mx;
  return { slope, intercept, r2: Pearson(xs, ys).r ** 2 };
}

export interface SurvivalSubject { time: number; event: boolean }
export interface SurvivalStep { time: number; survival: number }

export function KaplanMeier(subjects: SurvivalSubject[]): SurvivalStep[] {
  const sorted = [...subjects].sort((a, b) => a.time - b.time);
  const steps: SurvivalStep[] = [{ time: 0, survival: 1 }];
  let atRisk = sorted.length, survival = 1;
  for (const s of sorted) {
    if (s.event) {
      survival *= (atRisk - 1) / atRisk;
      const last = steps[steps.length - 1];
      if (last.time === s.time) last.survival = survival; // tied event times form one step
      else steps.push({ time: s.time, survival });
    }
    atRisk--;
  }
  return steps;
}

export function MedianSurvival(steps: SurvivalStep[]): number | null {
  return steps.find((s) => s.survival <= 0.5)?.time ?? null;
}

export function SurvivalAt(steps: SurvivalStep[], time: number): number {
  let value = 1;
  for (const s of steps) if (s.time <= time) value = s.survival;
  return value;
}

// Standard normal CDF (Abramowitz & Stegun 7.1.26 via erf).
function NormalCdf(z: number): number {
  const t = 1 / (1 + 0.3275911 * Math.abs(z) / Math.SQRT2);
  const erf = 1 - t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429)))) * Math.exp(-(z * z) / 2);
  return z >= 0 ? (1 + erf) / 2 : (1 - erf) / 2;
}

// Two-group log-rank test; returns the chi-square statistic and its 1-df p-value.
export function LogRank(a: SurvivalSubject[], b: SurvivalSubject[]) {
  const times = [...new Set([...a, ...b].filter((s) => s.event).map((s) => s.time))].sort((x, y) => x - y);
  let observedMinusExpected = 0, variance = 0;
  for (const t of times) {
    const na = a.filter((s) => s.time >= t).length, nb = b.filter((s) => s.time >= t).length;
    const da = a.filter((s) => s.event && s.time === t).length, d = da + b.filter((s) => s.event && s.time === t).length;
    const n = na + nb;
    if (n < 2) continue;
    observedMinusExpected += da - (d * na) / n;
    variance += (d * (na / n) * (nb / n) * (n - d)) / (n - 1);
  }
  const chi = observedMinusExpected ** 2 / variance;
  return { chi, p: 2 * (1 - NormalCdf(Math.sqrt(chi))) };
}

// Gaussian kernel density estimate at the given points (Silverman bandwidth).
export function KernelDensity(xs: number[], at: number[]): number[] {
  const h = 1.06 * Math.sqrt(Variance(xs)) * xs.length ** -0.2;
  const k = 1 / (xs.length * h * Math.sqrt(2 * Math.PI));
  return at.map((a) => k * xs.reduce((sum, x) => sum + Math.exp(-0.5 * ((a - x) / h) ** 2), 0));
}

// Inverse standard normal CDF (Acklam's rational approximation, |error| < 1.2e-9).
export function InverseNormal(p: number): number {
  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269, -30.66479806614716, 2.506628277459239];
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155288572];
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416];
  const tail = (q: number) => (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  if (p < 0.02425) return tail(Math.sqrt(-2 * Math.log(p)));
  if (p > 1 - 0.02425) return -tail(Math.sqrt(-2 * Math.log(1 - p)));
  const q = p - 0.5, r = q * q;
  return ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
}

export interface RocPoint { fpr: number; tpr: number; threshold: number }

// ROC curve from scores of positive and negative cases, plus trapezoidal AUC.
export function RocCurve(positives: number[], negatives: number[]) {
  const thresholds = [...positives, ...negatives].sort((x, y) => y - x);
  const points: RocPoint[] = [{ fpr: 0, tpr: 0, threshold: Infinity }];
  for (const t of thresholds) {
    points.push({ threshold: t, tpr: positives.filter((s) => s >= t).length / positives.length, fpr: negatives.filter((s) => s >= t).length / negatives.length });
  }
  const auc = points.slice(1).reduce((sum, p, i) => sum + (p.fpr - points[i].fpr) * (p.tpr + points[i].tpr) / 2, 0);
  return { points, auc };
}

export interface HistogramBin { start: number; end: number; count: number }

export function Histogram(xs: number[], binCount: number): HistogramBin[] {
  const min = Math.min(...xs), width = (Math.max(...xs) - min) / binCount;
  const bins = Array.from({ length: binCount }, (_, i) => ({ start: min + i * width, end: min + (i + 1) * width, count: 0 }));
  for (const x of xs) bins[Math.min(binCount - 1, Math.floor((x - min) / width))].count++;
  return bins;
}

// Average ranks (1-based), ties share their mean rank.
export function Ranks(xs: number[]): number[] {
  const order = xs.map((x, i) => [x, i] as const).sort((a, b) => a[0] - b[0]);
  const ranks = new Array<number>(xs.length);
  for (let i = 0; i < order.length; ) {
    let j = i;
    while (j + 1 < order.length && order[j + 1][0] === order[i][0]) j++;
    for (let k = i; k <= j; k++) ranks[order[k][1]] = (i + j) / 2 + 1;
    i = j + 1;
  }
  return ranks;
}

// Mann-Whitney U with the large-sample normal approximation (no tie correction).
export function MannWhitney(a: number[], b: number[]) {
  const ranks = Ranks([...a, ...b]);
  const rankSumA = ranks.slice(0, a.length).reduce((s, r) => s + r, 0);
  const u = rankSumA - (a.length * (a.length + 1)) / 2;
  const mean = (a.length * b.length) / 2;
  const sd = Math.sqrt((a.length * b.length * (a.length + b.length + 1)) / 12);
  const z = (u - mean) / sd;
  return { u, z, p: 2 * (1 - NormalCdf(Math.abs(z))) };
}

// Lower regularized incomplete gamma P(a, x) (Numerical Recipes gser / gcf).
function RegularizedGammaP(a: number, x: number): number {
  if (x <= 0) return 0;
  if (x < a + 1) {
    let sum = 1 / a, term = sum;
    for (let n = 1; n < 300; n++) { term *= x / (a + n); sum += term; if (Math.abs(term) < Math.abs(sum) * 1e-14) break; }
    return sum * Math.exp(-x + a * Math.log(x) - LogGamma(a));
  }
  let b = x + 1 - a, c = 1e30, d = 1 / b, h = d;
  for (let i = 1; i < 300; i++) {
    const an = -i * (i - a);
    b += 2; d = an * d + b; d = Math.abs(d) < 1e-30 ? 1e30 : 1 / d;
    c = b + an / c; c = Math.abs(c) < 1e-30 ? 1e-30 : c;
    h *= d * c;
  }
  return 1 - Math.exp(-x + a * Math.log(x) - LogGamma(a)) * h;
}

export function ChiSquareP(chi: number, df: number): number {
  return 1 - RegularizedGammaP(df / 2, chi / 2);
}

export function FTestP(f: number, df1: number, df2: number): number {
  return RegularizedBeta(df2 / (df2 + df1 * f), df2 / 2, df1 / 2);
}

export function OneWayAnova(groups: number[][]) {
  const all = groups.flat(), grand = Mean(all);
  const between = groups.reduce((s, g) => s + g.length * (Mean(g) - grand) ** 2, 0);
  const within = groups.reduce((s, g) => s + (g.length - 1) * Variance(g), 0);
  const df1 = groups.length - 1, df2 = all.length - groups.length;
  const f = between / df1 / (within / df2);
  return { f, df1, df2, p: FTestP(f, df1, df2) };
}

export function KruskalWallis(groups: number[][]) {
  const n = groups.reduce((s, g) => s + g.length, 0);
  const ranks = Ranks(groups.flat());
  let offset = 0, sum = 0;
  for (const g of groups) {
    const r = ranks.slice(offset, offset + g.length).reduce((s, v) => s + v, 0);
    sum += (r * r) / g.length;
    offset += g.length;
  }
  const h = (12 / (n * (n + 1))) * sum - 3 * (n + 1);
  const df = groups.length - 1;
  return { h, df, p: ChiSquareP(h, df) };
}

export function Spearman(xs: number[], ys: number[]) {
  const { r, p } = Pearson(Ranks(xs), Ranks(ys));
  return { rho: r, p };
}

// Kendall's tau-b with the normal approximation for its p-value.
export function Kendall(xs: number[], ys: number[]) {
  let concordant = 0, discordant = 0, tiesX = 0, tiesY = 0;
  for (let i = 0; i < xs.length; i++) {
    for (let j = i + 1; j < xs.length; j++) {
      const s = Math.sign(xs[i] - xs[j]) * Math.sign(ys[i] - ys[j]);
      if (s > 0) concordant++; else if (s < 0) discordant++;
      else if (xs[i] === xs[j]) tiesX++; else tiesY++;
    }
  }
  const n0 = (xs.length * (xs.length - 1)) / 2;
  const tau = (concordant - discordant) / Math.sqrt((n0 - tiesX) * (n0 - tiesY));
  const z = (3 * tau * Math.sqrt(xs.length * (xs.length - 1))) / Math.sqrt(2 * (2 * xs.length + 5));
  return { tau, p: 2 * (1 - NormalCdf(Math.abs(z))) };
}

// Chi-squared test of independence on an r x c table of counts.
export function ChiSquareIndependence(table: number[][]) {
  const rowTotals = table.map((r) => r.reduce((s, v) => s + v, 0));
  const colTotals = table[0].map((_, c) => table.reduce((s, r) => s + r[c], 0));
  const total = rowTotals.reduce((s, v) => s + v, 0);
  let chi = 0;
  table.forEach((row, r) => row.forEach((obs, c) => { const exp = (rowTotals[r] * colTotals[c]) / total; chi += (obs - exp) ** 2 / exp; }));
  const df = (table.length - 1) * (table[0].length - 1);
  return { chi, df, p: ChiSquareP(chi, df) };
}

// Single-predictor GLM fit by Newton-Raphson: `mean` is the inverse link, `weight` the variance at mu.
function FitGlm(xs: number[], ys: number[], mean: (eta: number) => number, weight: (mu: number) => number) {
  let b0 = 0, b1 = 0, i00 = 1, i01 = 0, i11 = 1;
  for (let iter = 0; iter < 50; iter++) {
    let g0 = 0, g1 = 0; i00 = 0; i01 = 0; i11 = 0;
    xs.forEach((x, i) => {
      const mu = mean(b0 + b1 * x), w = weight(mu);
      g0 += ys[i] - mu; g1 += (ys[i] - mu) * x;
      i00 += w; i01 += w * x; i11 += w * x * x;
    });
    const det = i00 * i11 - i01 * i01;
    const d0 = (i11 * g0 - i01 * g1) / det, d1 = (i00 * g1 - i01 * g0) / det;
    b0 += d0; b1 += d1;
    if (Math.abs(d0) + Math.abs(d1) < 1e-10) break;
  }
  const se1 = Math.sqrt(i00 / (i00 * i11 - i01 * i01));
  return { intercept: b0, slope: b1, se: se1, p: 2 * (1 - NormalCdf(Math.abs(b1 / se1))) };
}

const Logistic = (eta: number) => 1 / (1 + Math.exp(-eta));

export function LogisticFit(xs: number[], ys: number[]) {
  return FitGlm(xs, ys, Logistic, (mu) => mu * (1 - mu));
}

export function PoissonFit(xs: number[], ys: number[]) {
  return FitGlm(xs, ys, Math.exp, (mu) => mu);
}
